/**
 * Bản kê chi tiết lương của MỘT người trong MỘT tháng.
 *
 * Dùng chung cho hai chỗ: tab "Chi tiết lương" của chính nhân viên, và bảng
 * của quản lý/kế toán khi bấm vào một người. Cùng một thành phần nên hai bên
 * không thể nhìn thấy hai bản kê khác nhau của cùng một tháng — đó là điều
 * kiện để quản lý trả lời được thắc mắc của nhân viên.
 *
 * Nguyên tắc: màn hình này CHỈ VẼ LẠI. Mọi con số tiền đều do máy chủ gắn
 * sẵn cho từng dòng và có test khoá "cộng hết các dòng = số trên phiếu".
 */

import type { PayrollRecord } from "../../lib";
import { deptLabel, vnd, upsellCommissionLabel } from "../../constants";
import { AmountTable, DetailSection } from "./primitives";
import { DauSo } from "../../tap-chi/DauSo";
import { OrdersDetail } from "./OrdersDetail";
import { SurchargesDetail } from "./SurchargesDetail";
import {
  CrewHoursDetail,
  CskhDetail,
  RetouchDetail,
  UpsellItemsDetail,
} from "./WorkBreakdownDetail";
import { ReceptionDetail } from "./ReceptionDetail";
import { StudioAssistantDetail } from "./StudioAssistantDetail";
import { BonusDetail, OtherAmountsDetail } from "./BonusDetail";
import type { WorkLedgerRow } from "../../workLedgerApi";
import { SlipRows } from "../SlipRows";
import { HintNote } from "@/components/HintTip";

type Props = {
  record: PayrollRecord;
  roles?: string[] | undefined;
  month: string;
  /** Thời điểm máy chủ tính ra các con số này (ISO). */
  computedAt?: string | undefined;
  /**
   * Tháng đã chốt lương chưa — chốt rồi thì số không đổi nữa.
   *
   * Bỏ trống nghĩa là CHƯA BIẾT (bảng của quản lý lấy từ `GET /payroll`, nơi
   * này không kèm trạng thái chốt). Lúc đó ẩn hẳn nhãn, vì mặc định "Tạm tính"
   * trên một tháng đã chốt là nói sai ngay ở màn hình tiền.
   */
  finalized?: boolean | undefined;
  /**
   * Sổ từng job của Hậu kỳ / CSKH trong tháng.
   *
   * Người GỌI tự nạp rồi truyền xuống, vì hai chỗ dùng màn hình này nạp bằng
   * hai đường khác nhau: nhân viên xem sổ của mình qua `/work-ledger/me`
   * (không cần quyền), quản lý xem của người khác qua `/work-ledger` (cần
   * quyền `workledger.read`). Để màn hình tự gọi thì nhân viên nhận 403 ngay
   * trên bản kê của chính mình.
   *
   * Bỏ trống = chưa nạp / không có sổ; bản kê quay về mức số tổng như cũ.
   */
  ledger?: WorkLedgerRow[] | undefined;
  /**
   * Tỷ lệ hoa hồng bán thêm hiệu lực của tháng (từ response `upsell.commissionRate`),
   * dùng cho dòng chú thích "Trong đó bán thêm X%" ở bảng Tổng hợp. Bỏ trống
   * (màn dev mock) thì chú thích vẫn hiện số tiền, chỉ bớt phần %.
   */
  upsellCommissionRate?: number | undefined;
};

export function SalaryDetailView({
  record,
  roles = [],
  month,
  computedAt,
  finalized,
  ledger,
  upsellCommissionRate,
}: Props) {
  return (
    <div className="space-y-6">
      <Header
        record={record}
        roles={roles}
        month={month}
        computedAt={computedAt}
        finalized={finalized}
      />

      <div className="lt-trong-so">
        <h2>PHỤ LỤC</h2>
        <span className="lt-nhan">Từng việc trong tháng</span>
      </div>
      {/* Danh sách chuyên mục: số 01, 02… tự đếm theo các khối thật sự hiện. */}
      <div className="lt-ds-chuong">
        <SummaryTable record={record} upsellCommissionRate={upsellCommissionRate} />

        <OrdersDetail lines={record.orders ?? []} />
        <SurchargesDetail lines={record.surchargeItems ?? []} />
        <UpsellItemsDetail record={record} upsellCommissionRate={upsellCommissionRate} />
        <ReceptionDetail record={record} />
        <CrewHoursDetail record={record} />
        <StudioAssistantDetail record={record} />
        <RetouchDetail record={record} ledger={ledger} />
        <CskhDetail record={record} ledger={ledger} />
        <BonusDetail record={record} />
        <OtherAmountsDetail record={record} />
      </div>

      <p className="lt-nghieng px-1 text-[16px] text-[var(--lt-muc-2)]">
        Thấy số nào chưa đúng? Chụp lại màn hình này gửi trưởng phòng hoặc kế
        toán — bản kê có đủ ngày, mã đơn và tên khách để tra lại.
      </p>
    </div>
  );
}

function Header({
  record,
  roles,
  month,
  computedAt,
  finalized,
}: {
  record: PayrollRecord;
  roles: string[];
  month: string;
  computedAt?: string | undefined;
  finalized?: boolean | undefined;
}) {
  const [year, mon] = month.split("-");
  const phong =
    roles.length > 1 ? roles.map(deptLabel).join(" · ") : deptLabel(record.department ?? "");
  // Đầu màn kiểu tạp chí: số thực lĩnh LỚN, tên + phòng chữ nghiêng. Ảnh, tên đầy đủ đã ở
  // bìa "Lương của tôi"; bảng của quản lý mở bản này ngay dưới dòng tên người đó.
  return (
    <DauSo
      nhan={`Bảng kê tháng ${Number(mon)}/${year}`}
      so={record.totalPay ?? 0}
      loiMoi={
        <>
          {record.name} · {phong}
        </>
      }
      phuLuc={
        <div className="mt-3 space-y-2">
          {finalized != null ? (
            <span className="lt-chip font-semibold">{finalized ? "Đã chốt" : "Tạm tính"}</span>
          ) : null}
          {!finalized && computedAt ? (
            <p className="text-xs text-muted-foreground">
              Số tạm tính theo thời gian thực — cập nhật lúc{" "}
              {new Date(computedAt).toLocaleTimeString("vi-VN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              . Việc xong là cộng, không chờ cuối tháng — tiền chụp, trang điểm của một đơn cộng khi
              đơn đã sang Đang sửa ảnh.
            </p>
          ) : null}
          {/* Rào chuyển đổi bậc sắp hết hạn: máy chỉ gửi chữ khi còn kịp đi thi. */}
          {record.craftFloorWarning ? (
            <p
              role="alert"
              data-testid="detail-craft-floor"
              className="rounded border border-amber-300 bg-amber-50/70 px-2 py-1.5 text-xs text-amber-900"
            >
              {record.craftFloorWarning}
            </p>
          ) : null}
          {roles.length > 1 ? (
            <HintNote data-testid="detail-roles">
              Bạn làm ở {roles.length} phòng — bản kê dưới đây đã gộp cả {roles.length} phòng. Lương
              cứng chỉ tính theo phòng chính trong hồ sơ; phòng làm hộ chỉ tính tiền công.
            </HintNote>
          ) : null}
        </div>
      }
    />
  );
}

/**
 * Bảng tổng hợp — đúng các dòng in trên phiếu lương (cùng `SlipRows`), để người
 * đọc bắc cầu được từ phiếu sang bản kê. Khoản máy đã gộp (bán thêm, Phụ phí,
 * thưởng động lực) chỉ hiện ở dòng "trong đó", không cộng thêm lần nữa.
 */
function SummaryTable({
  record,
  upsellCommissionRate,
}: {
  record: PayrollRecord;
  upsellCommissionRate?: number | undefined;
}) {
  return (
    <DetailSection
      icon=""
      title="Tổng hợp"
      note="Đúng các dòng in trên phiếu lương — để bắc cầu từ phiếu sang bản kê."
      testId="detail-summary"
    >
      <AmountTable>
        <SlipRows
          record={record}
          variant="audit"
          dense
          {...(upsellCommissionRate != null
            ? { upsellNote: upsellCommissionLabel(upsellCommissionRate) }
            : {})}
        />
        <tr className="lt-ke-tong">
          <td className="px-1 py-2 md:px-2">Thực lĩnh</td>
          <td className="px-1 py-2 text-right tabular-nums md:px-2">
            {vnd(record.totalPay)}
          </td>
        </tr>
      </AmountTable>
      <HintNote className="mt-2">
        Dòng có mũi tên nằm <b>bên trong</b> dòng ngay trên, không phải khoản cộng
        thêm. Mục <b>Phát sinh</b> bên dưới liệt kê đủ từng khoản Phụ phí.
      </HintNote>
    </DetailSection>
  );
}
